
import React from 'react';
import { useCourierRentals } from '@/hooks/useCourierRentals';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Bike, Calendar, Clock, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '@/utils/dateUtils';

const CourierRentalsHistoryPage = () => {
  const navigate = useNavigate();
  
  // Получаем данные об арендах курьера
  const { rentals, isLoading, error } = useCourierRentals();
  
  // Сортируем аренды от новых к старым
  const sortedRentals = [...(rentals || [])].sort((a, b) => 
    new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );
  
  // Функция для форматирования времени
  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'HH:mm', { locale: ru });
    } catch (error) {
      console.error('Ошибка форматирования времени:', error, dateString);
      return '';
    }
  };
  
  // Вычисляем статус для отображения
  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Завершено';
      case 'active': return 'Активно';
      case 'cancelled': return 'Отменено';
      case 'pending': return 'Ожидает';
      default: return status;
    }
  };
  
  // Определяем вариант бейджа по статусу
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'active': return 'secondary';
      case 'cancelled': return 'destructive';
      case 'pending': return 'warning';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">История аренд</h1>
          <p className="text-muted-foreground">
            Полная история ваших аренд велосипедов
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate('/courier')}
          className="flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Назад</span>
        </Button>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>История аренд</CardTitle>
          <CardDescription>
            Все ваши аренды велосипедов
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-4 text-destructive">
              Произошла ошибка при загрузке данных
            </div>
          ) : sortedRentals.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              У вас пока нет истории аренд
            </div>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Дата</TableHead>
                    <TableHead>Велосипед</TableHead>
                    <TableHead>Номер</TableHead>
                    <TableHead>Стоимость</TableHead>
                    <TableHead>Статус</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRentals.map((rental) => (
                    <TableRow key={rental.id}>
                      <TableCell>
                        <div className="font-medium">{formatDate(rental.startDate)}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {formatTime(rental.startDate)}
                          {rental.endDate && ` - ${formatDate(rental.endDate)}`}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Bike className="h-4 w-4 text-muted-foreground" />
                          <span>{rental.bikeModel || 'Велосипед'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {rental.human_number ? (
                          <Badge variant="outline" className="bg-primary/10">
                            {rental.human_number}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            #{rental.id.substring(0, 6)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{rental.amount.toLocaleString()} ₽</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(rental.status) as any}>
                          {getStatusText(rental.status)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CourierRentalsHistoryPage;
